import { ArrowRightOutlined } from '@ant-design/icons'
import { Button } from 'antd'
import { t } from 'i18next'
import { useCallback, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { styled } from 'styled-components'
import { Badge, ModalBase } from '../../../../../components'
import {
  BadgeColors,
  workspaceDocumentSetStatuses,
} from '../../../../../constants/constants'
import {
  useSmartFunctionActions,
  useWorkspaceDetails,
} from '../../../../../hooks'
import { notificationService } from '../../../../../services'
import {
  ConformityCheckQualityReviewItem,
  ConformityChecksConfigDocumentSet,
  NotifyUsersOfSmartFunctionResultsBody,
  SmartFunctionResultLinkModel,
  WorkspaceConfig,
} from '../../../../../store/reducers/workspaceReducer'
import FlagDeviationsModalFooter from './footers/FlagDeviationsModalFooter'
const DropDownItem = styled.div`
  width: 200px;
`

const BaseSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
`

const DeviationsSection = styled(BaseSection)`
  margin-bottom: 32px;
`

const DocumentSection = styled(BaseSection)``

const SectionLabel = styled.div`
  font-size: 12px;
  font-weight: 600;
  width: 100%;
`

const ListContainer = styled.div`
  display: flex;
  flex-direction: column;
  background-color: ${(props) => props.theme.token.colorBgLayout};
  padding: 4px;
`

const SectionHelperText = styled.div`
  width: 100%;
  color: ${(props) => props.theme.token.colorWarningText};
`

const RowContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  padding: 4px 8px;
  min-height: 32px;
`

const NameContainer = styled.div`
  font-weight: 600;
  flex: 1;
  white-space: normal;
  word-break: break-word;
  padding-top: 4px;
`

const DeviationPageContainer = styled.div`
  width: 96px;
`

const DocumentStatusContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 4px;
  width: 160px;
  flex-shrink: 0;
  margin-left: 16px;
  padding-top: 4px;
`

const StyledButtonContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

interface FlagDeviationsModalProps {
  deviations?: ConformityCheckQualityReviewItem[]
}

const FlagDeviationsModal = ({ deviations }: FlagDeviationsModalProps) => {
  const { workspaceId, documentId, algorithmRunId } = useParams()
  const { selectedWorkspace, updateWorkspaceDetails } = useWorkspaceDetails({
    workspaceId,
    preventFetch: true,
  })
  const navigate = useNavigate()

  const { notifyUsersOfSmartFunctionResults } = useSmartFunctionActions({
    workspaceId,
    algorithmRunId,
  })

  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)

  const selectedDocument: ConformityChecksConfigDocumentSet | undefined =
    useMemo(
      () =>
        selectedWorkspace?.config?.conformityChecksConfig?.documents.find(
          (document) => document.id === documentId
        ),
      [selectedWorkspace, documentId]
    )

  const handleCancel = () => {
    setOpen(false)
  }

  const updatedDocuments = useMemo(() => {
    const config = selectedWorkspace?.config
    const documents = config?.conformityChecksConfig?.documents

    if (!documents) return []

    const updatedDocuments = documents.map((doc) =>
      doc.id === documentId
        ? {
            ...doc,
            documentSetStatus: workspaceDocumentSetStatuses.CLOSED,
          }
        : doc
    )

    return updatedDocuments
  }, [documentId, selectedWorkspace])

  const handleSubmit = async () => {
    setLoading(true)

    const updatedConfig = {
      ...selectedWorkspace?.config,
      conformityChecksConfig: {
        ...selectedWorkspace?.config?.conformityChecksConfig,
        flaggedDeviations: deviations,
        documents: updatedDocuments,
      },
    } as WorkspaceConfig

    const body: NotifyUsersOfSmartFunctionResultsBody = {
      sendTo: [selectedWorkspace?.config?.workspaceLead?.id || ''],
      resultLinkModel: SmartFunctionResultLinkModel.LABQR,
    }

    try {
      await updateWorkspaceDetails(updatedConfig)
      await notifyUsersOfSmartFunctionResults({
        body,
      })
      notificationService.notificationSuccess({
        messageToDisplay: t(
          `workspaceDetailsPage.workspaceSmartFunctionPanel.deviationConfirmationModal.documentClosedSuccessMessage`
        ),
      })
      notificationService.notificationSuccess({
        messageToDisplay: t(
          `workspaceDetailsPage.workspaceSmartFunctionPanel.deviationConfirmationModal.flagDeviationSuccessMessage`
        ),
      })
    } catch (error) {
      console.error('Error during update or notification', error)

      notificationService.notificationError({
        messageToDisplay: t(
          `workspaceDetailsPage.workspaceSmartFunctionPanel.deviationConfirmationModal.flagDeviationErrorMessage`
        ),
      })
    } finally {
      setLoading(false)
      setOpen(false)
    }
  }

  const deviationsForDocument = useMemo(() => {
    return deviations?.filter(
      (deviation) =>
        deviation.documentId === documentId &&
        deviation.algorithmRunId === algorithmRunId &&
        deviation.isDeviation
    )
  }, [deviations, documentId, algorithmRunId])

  const handleOpenModal = useCallback(() => {
    const workspaceLeadMissing =
      selectedWorkspace?.config?.workspaceLead?.id === undefined

    const documentSetStatus =
      selectedWorkspace?.config?.conformityChecksConfig?.documents?.find(
        (doc) => doc.id === documentId
      )?.documentSetStatus

    const noDeviationsFlagged = !deviationsForDocument?.length

    if (workspaceLeadMissing) {
      setOpen(false)
      notificationService.notificationError({
        messageToDisplay: (
          <StyledButtonContainer>
            <span>
              {t(
                'workspaceDetailsPage.workspaceSmartFunctionPanel.deviationConfirmationModal.alertNooWorkspaceLead'
              )}
            </span>
            <Button
              icon={<ArrowRightOutlined />}
              ghost
              danger
              size="small"
              onClick={() =>
                navigate(`/workspaceDetails/${workspaceId}/settings/info`)
              }
            >
              {t(
                'workspaceDetailsPage.workspaceSmartFunctionPanel.deviationConfirmationModal.goToSettings'
              )}
            </Button>
          </StyledButtonContainer>
        ),
      })
      return
    }

    if (documentSetStatus === workspaceDocumentSetStatuses.CLOSED) {
      setOpen(false)
      notificationService.notificationError({
        messageToDisplay: t(
          'workspaceDetailsPage.workspaceSmartFunctionPanel.deviationConfirmationModal.alertDocumentClosed'
        ),
      })
      return
    }

    if (noDeviationsFlagged) {
      setOpen(false)
      notificationService.notificationError({
        messageToDisplay: t(
          'workspaceDetailsPage.workspaceSmartFunctionPanel.deviationConfirmationModal.alertNoDeviationsFlagged'
        ),
      })
      return
    }

    setOpen(true)
  }, [
    selectedWorkspace,
    navigate,
    workspaceId,
    documentId,
    deviationsForDocument,
  ])

  const disabledDropdownItem = useMemo(() => {
    const documentSetStatus =
      selectedWorkspace?.config?.conformityChecksConfig?.documents?.find(
        (doc) => doc.id === documentId
      )?.documentSetStatus

    const noDeviationsFlagged = !deviationsForDocument?.length

    return (
      documentSetStatus === workspaceDocumentSetStatuses.CLOSED ||
      noDeviationsFlagged
    )
  }, [deviationsForDocument, selectedWorkspace, documentId])

 

  return (
    <>
      <DropDownItem
        onClick={handleOpenModal}
        style={{
          opacity: disabledDropdownItem ? 0.5 : 1,
        }}
      >
        {t(
          'workspaceDetailsPage.workspaceSmartFunctionPanel.actionMenu.flagDeviations'
        )}
      </DropDownItem>
      <ModalBase
        key="main"
        title={t(
          'workspaceDetailsPage.workspaceSmartFunctionPanel.deviationConfirmationModal.title'
        )}
        open={open}
        onOk={handleCancel}
        onCancel={handleCancel}
        forceRender
        confirmLoading={loading}
        footer={
          <FlagDeviationsModalFooter
            onCancel={handleCancel}
            onSubmit={handleSubmit}
          />
        }
      >
        <DeviationsSection>
          <SectionLabel>
            {t(
              'workspaceDetailsPage.workspaceSmartFunctionPanel.deviationConfirmationModal.deviationSectionLabel'
            )}
          </SectionLabel>
          <ListContainer>
            {deviationsForDocument?.map((deviation) => (
              <RowContainer key={deviation.id}>
                <NameContainer>{deviation.ofrBlockName}</NameContainer>
                <DeviationPageContainer>
                  {t(
                    'workspaceDetailsPage.workspaceSmartFunctionPanel.deviationConfirmationModal.deviationPage',
                    { pageNum: deviation.pageNum }
                  )}
                </DeviationPageContainer>
              </RowContainer>
            ))}
          </ListContainer>
          <SectionHelperText>
            {t(
              'workspaceDetailsPage.workspaceSmartFunctionPanel.deviationConfirmationModal.deviationSectionHelperText'
            )}
          </SectionHelperText>
        </DeviationsSection>
        <DocumentSection>
          <SectionLabel>
            {t(
              'workspaceDetailsPage.workspaceSmartFunctionPanel.deviationConfirmationModal.documentSectionLabel'
            )}
          </SectionLabel>
          <ListContainer>
            <RowContainer>
              <NameContainer>{selectedDocument?.title}</NameContainer>
              <DocumentStatusContainer>
                <Badge color={BadgeColors.LIME}>
                  {t(
                    `workspaceDetailsPage.documentSetStatus.${workspaceDocumentSetStatuses.OPENED}`
                  )}
                </Badge>
                <ArrowRightOutlined />
                <Badge color={BadgeColors.DEFAULT}>
                  {t(
                    `workspaceDetailsPage.documentSetStatus.${workspaceDocumentSetStatuses.CLOSED}`
                  )}
                </Badge>
              </DocumentStatusContainer>
            </RowContainer>
          </ListContainer>
          <SectionHelperText>
            {t(
              'workspaceDetailsPage.workspaceSmartFunctionPanel.deviationConfirmationModal.documentSectionHelperText'
            )}
          </SectionHelperText>
        </DocumentSection>
      </ModalBase>
    </>
  )
}

export default FlagDeviationsModal
